import { useCallback, useEffect, useRef, useState } from "react";
import { wsURL } from "./client";
import type { MeterView, Reading, WSMessage } from "./types";

export type LinkStatus = "connecting" | "online" | "offline";

export interface LiveData {
  meters: MeterView[];
  status: LinkStatus;
  /** Последний замер — по нему детальный экран дорисовывает точку на графике. */
  lastReading: Reading | null;
  /** true, если список счётчиков восстановлен из кэша, а живой связи ещё не было. */
  fromCache: boolean;
}

/** Пауза перед переподключением растёт до 15 секунд, чтобы не долбить упавший сервер. */
const reconnectDelays = [500, 1000, 2000, 5000, 10000, 15000];

/**
 * Ключ кэша последнего снимка счётчиков. Он делает приложение независимым от
 * подключения: при запуске без связи показываются последние сохранённые
 * значения, а не пустой экран. Оболочку кэширует service worker, данные — этот
 * снимок.
 */
const SNAPSHOT_KEY = "meters:snapshot:v1";

function loadSnapshot(): MeterView[] {
  try {
    const raw = localStorage.getItem(SNAPSHOT_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as MeterView[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return []; // приватный режим, повреждённый JSON — просто стартуем пустыми
  }
}

function saveSnapshot(meters: MeterView[]) {
  try {
    if (meters.length) localStorage.setItem(SNAPSHOT_KEY, JSON.stringify(meters));
  } catch {
    // Переполнение квоты или запрет хранилища не должны ронять UI.
  }
}

/**
 * useLive держит WebSocket-соединение с бэкендом и поддерживает актуальный
 * список счётчиков.
 *
 * Состояние собирается из snapshot'а при подключении и дальше обновляется
 * инкрементами. Отдельного REST-запроса за списком не нужно — это убирает
 * гонку «пришло событие по счётчику, которого ещё нет в списке».
 */
export function useLive(): LiveData {
  // Стартуем с последнего сохранённого снимка: даже без связи экран не пустой.
  const [meters, setMeters] = useState<MeterView[]>(loadSnapshot);
  const [status, setStatus] = useState<LinkStatus>("connecting");
  const [lastReading, setLastReading] = useState<Reading | null>(null);
  // Живого снимка ещё не было — показанное взято из кэша. Сбрасывается по WS-snapshot.
  const [fromCache, setFromCache] = useState(true);

  const socket = useRef<WebSocket | null>(null);
  const attempt = useRef(0);
  const timer = useRef<number | null>(null);
  const closed = useRef(false);

  const apply = useCallback((msg: WSMessage) => {
    switch (msg.type) {
      case "snapshot":
        setMeters(msg.meters ?? []);
        setFromCache(false); // пришли живые данные — кэш больше не «единственное, что есть»
        break;

      case "reading": {
        const r = msg.reading;
        if (!r) return;
        setLastReading(r);
        setMeters((prev) =>
          prev.map((m) =>
            m.id === r.meterId
              ? {
                  ...m,
                  latest: { ...m.latest, ...r.values },
                  latestTs: r.ts,
                  // Пришедший замер сам по себе означает, что связь есть:
                  // отдельное событие state сервер шлёт только при смене статуса.
                  state: { ...m.state, online: true, lastSeen: r.ts, lastError: "" },
                }
              : m,
          ),
        );
        break;
      }

      case "state": {
        const s = msg.state;
        if (!s) return;
        setMeters((prev) => prev.map((m) => (m.id === s.meterId ? { ...m, state: s } : m)));
        break;
      }
    }
  }, []);

  // Держим кэш в актуальном состоянии: следующий запуск без связи покажет
  // последние значения. Пишем не чаще, чем меняются данные (замер раз в 5 с).
  useEffect(() => {
    saveSnapshot(meters);
  }, [meters]);

  useEffect(() => {
    closed.current = false;

    const connect = () => {
      if (closed.current) return;
      setStatus((s) => (s === "online" ? "connecting" : s));

      const ws = new WebSocket(wsURL());
      socket.current = ws;

      ws.onopen = () => {
        attempt.current = 0;
        setStatus("online");
      };

      ws.onmessage = (e) => {
        try {
          apply(JSON.parse(e.data) as WSMessage);
        } catch (err) {
          console.error("не удалось разобрать сообщение WebSocket", err);
        }
      };

      ws.onclose = () => {
        if (closed.current) return;
        setStatus("offline");
        const delay = reconnectDelays[Math.min(attempt.current, reconnectDelays.length - 1)];
        attempt.current += 1;
        timer.current = window.setTimeout(connect, delay);
      };

      // onerror всегда сопровождается onclose — переподключение обрабатывается там.
      ws.onerror = () => ws.close();
    };

    connect();

    return () => {
      closed.current = true;
      if (timer.current !== null) window.clearTimeout(timer.current);
      socket.current?.close();
    };
  }, [apply]);

  return { meters, status, lastReading, fromCache };
}
